<template>
  <div class="lp-faculty-detail" v-if="faculty">
    <lp-avatar class="lp-fd-portrait" :name="faculty.name" :photo="faculty.photo" />

    <h2 class="lp-fd-name">{{ faculty.name }}</h2>
    <p v-if="faculty.role" class="lp-fd-role">{{ faculty.role }}</p>

    <div v-if="faculty.bio" class="lp-fd-section lp-fd-section--first">
      <h3 class="lp-fd-section-label">Biography</h3>
      <div class="lp-fd-bio" v-html="faculty.bio"></div>
    </div>

    <div v-if="sessionList.length" class="lp-fd-section">
      <h3 class="lp-fd-section-label">{{ sessionsLabel }} ({{ sessionList.length }})</h3>
      <div class="lp-fd-sessions">
        <button
          v-for="(session, index) in sessionList"
          :key="session._id || index"
          type="button"
          class="lp-fd-session"
          @click="$emit('open-session', session)"
        >
          <span class="lp-fd-session-meta">
            <span v-if="sessionTime(session)" class="lp-fd-session-time">{{ sessionTime(session) }}</span>
            <span v-if="session.trackLabel" class="lp-fd-session-track" :data-track="session.trackColor">{{ session.trackLabel }}</span>
          </span>
          <span class="lp-fd-session-title" v-html="session.title"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LpAvatar from './lp-avatar'

/**
 * Faculty / presenter detail — the body of the speaker drawer: portrait (photo
 * when available, initials otherwise), name, role, biography and the sessions
 * this person presents.
 *
 * Shared by the multiple-theme landing page (components/modals.blade.php) and
 * the registration page (pages/registration/index.vue). Both pass a record from
 * ConferenceLandingPageService::transformPresenterForNewTheme — the landing page
 * from /event/faculty, the registration page from the same endpoint — so the two
 * surfaces show the same information.
 *
 * `sessions` overrides `faculty.sessions` for hosts that resolve the list
 * themselves; `dayNames` maps a session's day id to its label ("Day 1 · Mar 4"),
 * of which only the part before the separator is shown.
 */
export default {
  name: 'lp-faculty-detail',

  components: { LpAvatar },

  props: {
    faculty: { type: Object, default: null },
    sessions: { type: Array, default: null },
    dayNames: { type: Object, default: () => ({}) },
    sessionsLabel: { type: String, default: 'Presenting at this conference' },
  },

  computed: {
    sessionList() {
      if (this.sessions) return this.sessions
      return (this.faculty && this.faculty.sessions) || []
    },
  },

  methods: {
    // "Day 1 · Mar 4" -> "Day 1", joined with the session's time range. Either
    // half may be missing (single-day events carry no day label).
    sessionTime(session) {
      const label = (this.dayNames[session.day] || '').split('·')[0].trim()
      return [label, session.time].filter(Boolean).join(' · ')
    },
  },
}
</script>

<style lang="scss" scoped>
// Round/clipped/centred comes from <lp-avatar>; size, spacing and the sheen
// are this surface's own.
.lp-fd-portrait {
  width: 140px;
  height: 140px;
  // decorative numeral, not tokenized (avatar initials)
  font-size: 38px;
  margin-bottom: 24px;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.18), transparent 60%);
    pointer-events: none;
  }
}

.lp-fd-name {
  font-size: var(--fs-h3);
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 0 0 6px;
  color: var(--text);
  line-height: 1.25;
}

.lp-fd-role {
  font-size: var(--fs-small);
  color: var(--text-secondary);
  margin: 0 0 18px;
  line-height: 1.4;
}

.lp-fd-section {
  margin-top: 28px;

  &--first { margin-top: 0; }
}

.lp-fd-section-label {
  font-size: var(--fs-caption);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--brand-deep);
  font-weight: 500;
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid var(--border);
}

// Bios arrive as HTML from the organizer, so the paragraph rules have to
// reach past the scope attribute that v-html children never receive.
.lp-fd-bio ::v-deep p {
  font-size: var(--fs-body);
  line-height: 1.65;
  color: var(--text);
  margin: 0 0 14px;

  &:last-child { margin-bottom: 0; }
}

.lp-fd-sessions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lp-fd-session {
  display: block;
  width: 100%;
  padding: 14px 16px;
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-md);
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--brand-light);
    background: var(--surface-alt);
  }
}

.lp-fd-session-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.lp-fd-session-time {
  font-size: var(--fs-caption);
  color: var(--brand-deep);
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.lp-fd-session-track {
  font-size: var(--fs-micro);
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--surface-alt);
  color: var(--text-secondary);

  &[data-track="1"] { background: #e8eee5; color: #344a2f; }
  &[data-track="2"] { background: #e0eaf2; color: #1e3a5f; }
  &[data-track="3"] { background: #e1f5ee; color: #08503d; }
  &[data-track="4"] { background: #faece7; color: #712b13; }
  &[data-track="5"] { background: #eeedfe; color: #3c3489; }
}

.lp-fd-session-title {
  display: block;
  font-size: var(--fs-body);
  font-weight: 500;
  color: var(--text);
  line-height: 1.4;
}

@media (max-width: 800px) {
  .lp-fd-portrait {
    width: 110px;
    height: 110px;
    // decorative numeral, not tokenized (avatar initials)
    font-size: 30px;
  }
}
</style>
